import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { ObligacionAmbiental, useObligaciones } from "@/hooks/useObligaciones";
import { FileText, Upload, Eye, Download, Edit, Save, X, Trash2, Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import EvidenciasManager, { EvidenciaArchivo } from "./EvidenciasManager";
import PlanManejoManager, { PlanManejoManagerRef } from "./PlanManejoManager";
import { useModalPersistence } from "@/hooks/useModalPersistence";
import { useFormPersistence } from "@/hooks/useStatePersistence";

interface DetalleObligacionModalProps {
  obligacion: ObligacionAmbiental | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (obligacion: ObligacionAmbiental) => void;
  onRefresh: () => void;
  onDelete?: (id: string) => void;
  activeTab?: string;
}

const DetalleObligacionModal = ({ 
  obligacion, 
  isOpen, 
  onClose, 
  onUpdate,
  onRefresh,
  onDelete,
  activeTab = 'detalles'
}: DetalleObligacionModalProps) => {
  const { toast } = useToast();
  const { deleteObligacion } = useObligaciones();
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editedObligacion, setEditedObligacion] = useState<ObligacionAmbiental | null>(null);
  const [metas, setMetas] = useState<string[]>([]);
  const { modalState, setActiveTab } = useModalPersistence();
  const planManejoRef = useRef<PlanManejoManagerRef>(null);
  
  // Persistir estado de edición
  const { values: editingState, updateField: updateEditingState, resetForm: resetEditingState } = useFormPersistence(
    `edicion_obligacion_${obligacion?.id || 'nueva'}`,
    {
      isEditing: false,
      editedData: null as ObligacionAmbiental | null,
      metas: [] as string[]
    },
    user?.id
  );

  // Restaurar estado de edición al abrir
  useEffect(() => {
    if (isOpen && editingState.isEditing && editingState.editedData) {
      setIsEditing(true);
      setEditedObligacion(editingState.editedData);
      setMetas(editingState.metas);
    }
  }, [isOpen]);

  // Guardar estado de edición
  useEffect(() => {
    if (isEditing) {
      updateEditingState('isEditing', true);
      updateEditingState('editedData', editedObligacion);
      updateEditingState('metas', metas);
    }
  }, [isEditing, editedObligacion, metas]);
  
  // Usar la pestaña activa del estado persistente o el prop
  const currentTab = modalState.activeTab || activeTab;

  // Inicializar metas desde observaciones al cargar
  useEffect(() => {
    if (obligacion?.observaciones) {
      try {
        const parsedMetas = JSON.parse(obligacion.observaciones);
        if (Array.isArray(parsedMetas)) {
          setMetas(parsedMetas);
        } else {
          setMetas([obligacion.observaciones]);
        }
      } catch {
        setMetas([obligacion.observaciones]);
      }
    } else {
      setMetas([]);
    }
  }, [obligacion]);

  const handleEdit = () => {
    setEditedObligacion(obligacion ? { ...obligacion } : null);
    if (obligacion?.observaciones) {
      try {
        const parsedMetas = JSON.parse(obligacion.observaciones);
        if (Array.isArray(parsedMetas)) {
          setMetas(parsedMetas);
        } else {
          setMetas([obligacion.observaciones]);
        }
      } catch {
        setMetas([obligacion.observaciones]);
      }
    }
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      // Si estamos en la pestaña de Plan de Manejo, guardar el plan
      if (currentTab === 'seguimiento' && planManejoRef.current) {
        await planManejoRef.current.savePlanData();
      }
      
      // Guardar los datos de la obligación si hay cambios
      if (editedObligacion) {
        const updatedObligacion = {
          ...editedObligacion,
          observaciones: JSON.stringify(metas.filter(m => m.trim() !== ''))
        };
        await Promise.resolve(onUpdate(updatedObligacion));
      }
      
      // Salir del modo de edición después de guardar exitosamente
      setIsEditing(false);
      resetEditingState();
      
      toast({
        title: "Guardado exitoso",
        description: "Los cambios se han guardado correctamente."
      });
    } catch (err: any) {
      console.error('Error saving:', err);
      toast({
        title: "Error al guardar",
        description: err?.message || "No se pudieron guardar los cambios",
        variant: "destructive"
      });
    }
  };

  const handleCancel = () => {
    setEditedObligacion(null);
    setIsEditing(false);
    resetEditingState();
    // Restaurar metas originales
    if (obligacion?.observaciones) {
      try {
        const parsedMetas = JSON.parse(obligacion.observaciones);
        if (Array.isArray(parsedMetas)) {
          setMetas(parsedMetas);
        } else {
          setMetas([obligacion.observaciones]);
        }
      } catch {
        setMetas([obligacion.observaciones]);
      }
    }
  };

  const handleDelete = async () => {
    if (!obligacion) return;
    
    try {
      await deleteObligacion(obligacion.id);
      onRefresh(); // Refresh the parent list
      onClose(); // Close the modal
      toast({
        title: "Obligación eliminada",
        description: "La obligación se ha eliminado correctamente."
      });
    } catch (error) {
      // Error is handled in the hook
    }
  };

  const handleInputChange = (field: string, value: string | number) => {
    if (editedObligacion) {
      setEditedObligacion({ ...editedObligacion, [field]: value });
    }
  };

  const handleAddMeta = () => {
    setMetas([...metas, '']);
  };

  const handleMetaChange = (index: number, value: string) => {
    const newMetas = [...metas];
    newMetas[index] = value;
    setMetas(newMetas);
  };

  const handleRemoveMeta = (index: number) => {
    setMetas(metas.filter((_, i) => i !== index));
  };


  const handleEvidenciasUpdate = (evidencias: EvidenciaArchivo[]) => {
    // TODO: Implementar gestión de evidencias en base de datos
    console.log('Evidencias actualizadas:', evidencias);
  };

  const getEstadoBadge = (estado: string) => {
    const variants = {
      'Cumplido': 'default',
      'En Proceso': 'secondary',
      'Pendiente': 'outline',
      'Vencido': 'destructive'
    } as const;

    return (
      <Badge variant={variants[estado as keyof typeof variants] || 'outline'}>
        {estado}
      </Badge>
    );
  };

  const getTipoBadge = (tipo: string) => {
    const colors = {
      'Ambiental': 'bg-environmental/10 text-environmental border-environmental/20',
      'Social': 'bg-blue-100 text-blue-700 border-blue-200',
      'Biótico': 'bg-green-100 text-green-700 border-green-200'
    };

    return (
      <Badge className={colors[tipo as keyof typeof colors] || ''}>
        {tipo}
      </Badge>
    );
  };

  if (!obligacion) return null;

  const currentObligacion = isEditing ? editedObligacion : obligacion;
  if (!currentObligacion) return null;

  const handleClose = () => {
    setIsEditing(false);
    setEditedObligacion(null);
    resetEditingState();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {currentObligacion.codigo} - {currentObligacion.nombre_programa}
            </DialogTitle>
            <div className="flex gap-2">
              {!isEditing ? (
                <>
                  <Button onClick={handleEdit} size="sm" variant="outline">
                    <Edit className="w-4 h-4 mr-2" />
                    Editar
                  </Button>
                </>
              ) : (
                <div className="flex gap-2">
                  <Button onClick={handleSave} size="sm">
                    <Save className="w-4 h-4 mr-2" />
                    Guardar
                  </Button>
                  <Button onClick={handleCancel} size="sm" variant="outline">
                    <X className="w-4 h-4 mr-2" />
                    Cancelar
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="destructive">
                        <Trash2 className="w-4 h-4 mr-2" />
                        Eliminar
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Esta acción no se puede deshacer. Esto eliminará permanentemente la obligación "{currentObligacion.codigo}" y todos sus datos asociados.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                          Eliminar
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </div>
          </div>
        </DialogHeader>

        <Tabs value={currentTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="detalles">Detalles</TabsTrigger>
            <TabsTrigger value="seguimiento">Plan de Manejo</TabsTrigger>
          </TabsList>

          <TabsContent value="detalles" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Información Básica */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Información Básica</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Código</Label>
                    {isEditing ? (
                      <Input
                        value={currentObligacion.codigo}
                        onChange={(e) => handleInputChange('codigo', e.target.value)}
                      />
                    ) : (
                      <p className="font-medium">{currentObligacion.codigo}</p>
                    )}
                  </div>

                  <div>
                    <Label>Nombre del Programa</Label>
                    {isEditing ? (
                      <Input
                        value={currentObligacion.nombre_programa}
                        onChange={(e) => handleInputChange('nombre_programa', e.target.value)}
                      />
                    ) : (
                      <p>{currentObligacion.nombre_programa}</p>
                    )}
                  </div>

                  <div>
                    <Label>Tipo</Label>
                    {isEditing ? (
                      <Select
                        value={currentObligacion.tipo}
                        onValueChange={(value) => handleInputChange('tipo', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Ambiental">Ambiental</SelectItem>
                          <SelectItem value="Social">Social</SelectItem>
                          <SelectItem value="Biótico">Biótico</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <div>{getTipoBadge(currentObligacion.tipo)}</div>
                    )}
                  </div>

                  <div>
                    <Label>Objetivo</Label>
                    {isEditing ? (
                      <Textarea
                        value={currentObligacion.objetivo}
                        onChange={(e) => handleInputChange('objetivo', e.target.value)}
                        rows={3}
                      />
                    ) : (
                      <p>{currentObligacion.objetivo}</p>
                    )}
                  </div>

                  <div>
                    <Label>Frecuencia</Label>
                    {isEditing ? (
                      <Select
                        value={currentObligacion.frecuencia}
                        onValueChange={(value) => handleInputChange('frecuencia', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Continuo">Continuo</SelectItem>
                          <SelectItem value="Mensual">Mensual</SelectItem>
                          <SelectItem value="Trimestral">Trimestral</SelectItem>
                          <SelectItem value="Anual">Anual</SelectItem>
                          <SelectItem value="Única">Única</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <p>{currentObligacion.frecuencia}</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Responsabilidades y Estado */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Responsabilidades y Estado</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Entidad de Reporte</Label>
                    {isEditing ? (
                      <Input
                        value={currentObligacion.entidad_reporte}
                        onChange={(e) => handleInputChange('entidad_reporte', e.target.value)}
                      />
                    ) : (
                      <p>{currentObligacion.entidad_reporte}</p>
                    )}
                  </div>

                  <div>
                    <Label>Responsable Interno</Label>
                    {isEditing ? (
                      <Input
                        value={currentObligacion.responsable_interno}
                        onChange={(e) => handleInputChange('responsable_interno', e.target.value)}
                      />
                    ) : (
                      <p>{currentObligacion.responsable_interno}</p>
                    )}
                  </div>

                  <div>
                    <Label>Estado</Label>
                    {isEditing ? (
                      <Select
                        value={currentObligacion.estado}
                        onValueChange={(value) => handleInputChange('estado', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Pendiente">Pendiente</SelectItem>
                          <SelectItem value="En Proceso">En Proceso</SelectItem>
                          <SelectItem value="Cumplido">Cumplido</SelectItem>
                          <SelectItem value="Vencido">Vencido</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <div>{getEstadoBadge(currentObligacion.estado)}</div>
                    )}
                  </div>

                  <div>
                    <Label>Vencimiento</Label>
                    {isEditing ? (
                      <Input
                        type="date"
                        value={currentObligacion.vencimiento}
                        onChange={(e) => handleInputChange('vencimiento', e.target.value)}
                      />
                    ) : (
                      <p>{currentObligacion.vencimiento}</p>
                    )}
                  </div>

                  <div>
                    <Label>Nivel de Cumplimiento (%)</Label>
                    {isEditing ? (
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={currentObligacion.nivel_cumplimiento}
                        onChange={(e) => handleInputChange('nivel_cumplimiento', parseInt(e.target.value) || 0)}
                      />
                    ) : (
                      <p>{currentObligacion.nivel_cumplimiento}%</p>
                    )}
                  </div>

                  <div>
                    <Label>Plazo de Cumplimiento</Label>
                    {isEditing ? (
                      <Input
                        value={currentObligacion.plazo_cumplimiento}
                        onChange={(e) => handleInputChange('plazo_cumplimiento', e.target.value)}
                      />
                    ) : (
                      <p>{currentObligacion.plazo_cumplimiento}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>


            {/* Metas */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Metas</CardTitle>
                {isEditing && (
                  <Button onClick={handleAddMeta} size="sm" variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Meta
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                {isEditing ? (
                  metas.length === 0 ? (
                    <p className="text-muted-foreground text-sm">No hay metas agregadas. Haz clic en "Agregar Meta" para comenzar.</p>
                  ) : (
                    metas.map((meta, index) => (
                      <div key={index} className="flex gap-2">
                        <Textarea
                          value={meta}
                          onChange={(e) => handleMetaChange(index, e.target.value)}
                          rows={2}
                          placeholder={`Meta ${index + 1}...`}
                          className="flex-1"
                        />
                        <Button
                          onClick={() => handleRemoveMeta(index)}
                          size="icon"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))
                  )
                ) : (
                  <div className="space-y-2">
                    {metas.length === 0 ? (
                      <p className="text-muted-foreground">Sin metas</p>
                    ) : (
                      metas.map((meta, index) => (
                        <div key={index} className="flex gap-2 items-start">
                          <span className="font-medium text-sm mt-1">{index + 1}.</span>
                          <p className="flex-1">{meta}</p>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="seguimiento" className="space-y-4">
            <PlanManejoManager 
              ref={planManejoRef}
              obligacionId={currentObligacion.id}
              isEditing={isEditing}
              onSaveSuccess={() => {
                setIsEditing(false);
                resetEditingState();
              }}
            />
          </TabsContent>

        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default DetalleObligacionModal;